import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

import { useMe } from 'src/hooks'

import { formatDate } from './format-date'
import { useStyles } from './styles'
import { IStripeCharge } from 'src/types'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { FONTS } from 'src/constants'

interface IProps {
  transactions: IStripeCharge[]
}

const BillingTransactionsTable = ({ transactions }: IProps) => {
  const { data: user } = useMe()
  const classes = useStyles()
  const theme = useTheme()

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={classes.invoicesHeadCell} align="center">
              <HelperWrapper t="email-logs-date-column" />
            </TableCell>
            <TableCell className={classes.invoicesHeadCell} align="center">
              <HelperWrapper t="transactions-amount-column" />
            </TableCell>
            <TableCell className={classes.invoicesHeadCell} align="center">
              <HelperWrapper t="transactions-place-column" />
            </TableCell>
            <TableCell className={classes.invoicesHeadCell} align="center" />
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map(({ id, created, amount, status, receipt_url, invoice, metadata }) => (
            <TableRow key={id} className={classes.invoiceRow}>
              <TableCell className={classes.invoiceBodyCell}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {formatDate(created * 1000, user?.timezone)}
                  {metadata?.plan_end && (
                    <Typography
                      sx={{
                        margin: 0,
                        fontSize: FONTS.SIZES.xs,
                        lineHeight: 1.4,
                        color: theme.palette.custom.grey2,
                      }}
                    >
                      Expires on: {formatDate(metadata.plan_end * 1000, user?.timezone)}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell className={classes.invoiceBodyCell}>
                ${amount / 100}{' '}
                <div
                  className={`${classes.inoviceAmountStatus} ${
                    status === 'succeeded'
                      ? classes.invoiceAmountStatusSuccess
                      : classes.invoiceAmountStatusFailure
                  }`}
                >
                  <p className={classes.invoiceStatus}>{status}</p>
                </div>
              </TableCell>
              <TableCell
                className={`${classes.invoiceBodyCell}`}
                sx={{ textTransform: 'capitalize' }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {metadata.subscription_plan}
                  {metadata?.plan_duration && (
                    <Typography
                      sx={{
                        margin: 0,
                        fontSize: FONTS.SIZES.xs,
                        lineHeight: 1.4,
                        color: theme.palette.custom.grey2,
                      }}
                    >
                      {metadata.plan_duration === 'Month' ? (
                        <HelperWrapper t="transaction-history-table-plan-duration-month" />
                      ) : (
                        <HelperWrapper t="transaction-history-table-plan-duration-year" />
                      )}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell className={`${classes.invoiceBodyCell}`}>
                <Button
                  disabled={!invoice?.hosted_invoice_url}
                  href={invoice?.hosted_invoice_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  component={'a'}
                  variant={'text'}
                  size="small"
                  className={`${classes.invoiceLink}`}
                >
                  <HelperWrapper t="view-button-text" />
                </Button>{' '}
                |{' '}
                <Button
                  disabled={!invoice?.invoice_pdf}
                  href={invoice?.invoice_pdf}
                  download
                  component={'a'}
                  variant={'text'}
                  size="small"
                  className={`${classes.invoiceLink}`}
                >
                  <HelperWrapper t="invoice-button-text" />
                </Button>{' '}
                |{' '}
                <Button
                  disabled={!receipt_url}
                  href={receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  component={'a'}
                  variant={'text'}
                  size="small"
                  className={`${classes.invoiceLink}`}
                >
                  <HelperWrapper t="receipt-button-text" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export { BillingTransactionsTable }
